import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DollarSign, Check, Sparkles, Wallet } from 'lucide-react';
import { withoutRetiredMilesMethod } from '@/utils';

// mobileLabel shows under 640px, where "Installments" ran into the pill's
// edge beside the travelers and cabin pills (founder, 10-22: "Payments").
const paymentMethods = [
  { value: 'installments', label: 'Installments', mobileLabel: 'Payments', icon: DollarSign, description: 'Pay in installments' },
  { value: 'reserve', label: 'Reserve', mobileLabel: 'Reserve', icon: Wallet, description: 'Pay with Reserve credits' }
];

export default function PaymentMethodSelector({ value, onChange, className, milesBalance = 0, reserveBalanceCents = 0, hasStay = false, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  // "Miles" and "Miles + Installments" were payment methods until 2026-10-05;
  // Miles are applied at checkout now. A selection still holding one is
  // moved back to installments.
  useEffect(() => {
    const current = withoutRetiredMilesMethod(value);
    if (current !== value) onChange?.(current);
  }, [value, onChange]);

  const selectedMethod = paymentMethods.find(m => m.value === value) || paymentMethods[0];
  const SelectedIcon = selectedMethod.icon;

  const handleSelect = (methodValue) => {
    onChange(methodValue);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Popover open={disabled ? false : isOpen} onOpenChange={(open) => !disabled && setIsOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            disabled={disabled}
            className={`w-full justify-start text-left text-base md:text-sm font-normal ${className} pl-9 max-[374px]:pl-8 pr-2 sm:pr-3 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {/* Same icon spot, gap and tint as the travelers and cabin pills (all
                three move in 4px under 375px wide). */}
            <SelectedIcon className="absolute left-3 max-[374px]:left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-current opacity-80" />
            <span className="hidden sm:inline">{selectedMethod.label}</span>
            <span className="sm:hidden">{selectedMethod.mobileLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 bg-black/95 border-white/20 text-white p-2 backdrop-blur-xl" align="center" side="bottom" sideOffset={8}>
          {(milesBalance > 0 || reserveBalanceCents > 0) && (
            <div className="px-3 py-2 mb-2 bg-white/5 rounded-md border border-white/10 space-y-1.5">
              {milesBalance > 0 && (
                <div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-brand-400" />
                    <span className="text-xs text-white/70">Available Miles:</span>
                    <span className="text-sm font-medium text-white">{milesBalance.toLocaleString()}</span>
                  </div>
                </div>
              )}
              {reserveBalanceCents > 0 && (
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs text-white/70">Reserve Balance:</span>
                  <span className="text-sm font-medium text-white">${(reserveBalanceCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>
          )}
          <div className="space-y-1">
            {paymentMethods.map((method) => {
              const Icon = method.icon;
              const isReserveOption = method.value === 'reserve';
              // Defera Reserve was discontinued for new users — hide the option entirely
              // unless the customer has a wallet balance to spend.
              if (isReserveOption && reserveBalanceCents <= 0) {
                return null;
              }
              const isDisabled = isReserveOption && hasStay;
              const displayDescription = isDisabled ? 'Not available for Flights + Stays' : method.description;

              return (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => !isDisabled && handleSelect(method.value)}
                  disabled={isDisabled}
                  className={`w-full px-3 py-2 text-left hover:bg-white/10 transition-colors duration-200 flex items-start justify-between rounded-md ${
                    isDisabled ? 'opacity-40 cursor-not-allowed' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className="w-4 h-4 mt-0.5 text-white/70" />
                    <div>
                      <div className="text-sm text-white">{method.label}</div>
                      <div className="text-xs text-white/60">{displayDescription}</div>
                    </div>
                  </div>
                  {value === method.value && (
                    <Check className="w-4 h-4 text-white flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
